/**
 * @module AEM
 * @copyright 1994-2017 T-Mobile US, Inc. All Rights Reserved.
 */ /** */

// Core Libraries
import { Component, OnInit, OnDestroy, EventEmitter } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
// Interfaces
import { ComparisonChart } from '@core/aem/components/comparison-chart/models/comparison-chart.interface';
import { AuthorableComponent } from '@core/aem/models/authorable-component.model';
import { DynamicModal } from '@support/shared/widgets/interfaces/dynamic-modal.interface';
import { BrowsePlanConfig } from '@core/aem/interfaces/shop/browse-plan.interface';

// Services
import { PlanDetailsService } from '@experience/shop/plan-details/services/plan-details.service';

@Component({
  selector: 'tmo-comparison-chart',
  templateUrl: './comparison-chart.component.html',
})
export class ComparisonChartComponent
  extends AuthorableComponent<ComparisonChart>
  implements OnInit, OnDestroy
{
  /**
   * Authorable component data
   */
  public authorableData: ComparisonChart;
  /**
   * Modal data
   */
  public modal: DynamicModal;

  /**
   * Stores value aem content
   */
  public planDetailAEMContent: BrowsePlanConfig;
  /**
   * Next. Complete. Unsubscribe.
   */
  private ngUnsubscribe: Subject<void>;

  constructor(private planDetailsService: PlanDetailsService) {
    super();
    this.ngUnsubscribe = new Subject<void>();
    this.modal = {
      disableDismiss: false,
      disableEsc: true,
      toggleModalEmitter: new EventEmitter(),
      dismissableMask: true,
      modalName: 'comparison-chart-modal',
    };
  }
  /**
   * Component lifecycle hook to initialize the component
   */
  public ngOnInit(): void {
    this.loadAEMData();
  }
  /**
   * Unsubscribes for all subscriptions
   */
  public ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }
  /**
   * Method to close Chart Comparison Modal.
   * @returns void
   */
  public closeModal(): void {
    this.modal.toggleModalEmitter.emit({ close: true });
  }
  /**
   * Method to open the modal on click of Chart Comparison link.
   * @returns void
   */
  public openModal(): void {
    this.modal.toggleModalEmitter.emit({ open: true });
  }
  /**
   * Method to set data in CoverageMapAuthorable
   * @param data : this will store the data that need to be set in ChartComparisonAuthorable
   * @returns void
   */
  public setData(data: ComparisonChart): void {
    if (this.validate(data)) {
      this.authorableData = data;
    }
  }

  /**
   * Check if the data is valid, should throw an error if the input doesn't match designated fields
   * @param data : data that needs to be validated
   * @returns boolean
   */
  public validate(data: ComparisonChart): boolean {
    if (
      data &&
      data.prefixText !== null &&
      data.ctaText != null &&
      data.suffixText != null &&
      data.modalTitle != null &&
      data.modalBody != null
    ) {
      return true;
    } else {
      throw new Error(`ComparisonChartComponent: Invalid params ${data}`);
    }
  }
  /**
   * Load AEM Data
   * return void
   */
  private loadAEMData(): void {
    this.planDetailsService
      .getAemContent()
      .pipe(takeUntil(this.ngUnsubscribe.asObservable()))
      .subscribe((data: BrowsePlanConfig) => {
        this.planDetailAEMContent = data;
      });
  }
}
